import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { en } from '../i18n/locales/en';
import { tr } from '../i18n/locales/tr';
import { APP_CONFIG } from '../utils/constants';

export type Language = 'en' | 'tr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getDataGridLocaleText: () => Record<string, unknown>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

const translations = { en, tr } as const;
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('tr'); // Default to Turkish

  useEffect(() => {
    // Load language from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'tr')) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  const getDataGridLocaleText = () => {
    return {
      // No rows overlay
      noRowsLabel: t('dataGrid.noRowsLabel'),
      noResultsOverlayLabel: t('dataGrid.noResultsOverlayLabel'),
      errorOverlayDefaultLabel: t('dataGrid.errorOverlayDefaultLabel'),

      // Density selector toolbar button text
      toolbarDensity: t('dataGrid.toolbarDensity'),
      toolbarDensityLabel: t('dataGrid.toolbarDensityLabel'),
      toolbarDensityCompact: t('dataGrid.toolbarDensityCompact'),
      toolbarDensityStandard: t('dataGrid.toolbarDensityStandard'),
      toolbarDensityComfortable: t('dataGrid.toolbarDensityComfortable'),

      // Columns selector toolbar button text
      toolbarColumns: t('dataGrid.toolbarColumns'),
      toolbarColumnsLabel: t('dataGrid.toolbarColumnsLabel'),

      // Filters toolbar button text
      toolbarFilters: t('dataGrid.toolbarFilters'),
      toolbarFiltersLabel: t('dataGrid.toolbarFiltersLabel'),
      toolbarFiltersTooltipHide: t('dataGrid.toolbarFiltersTooltipHide'),
      toolbarFiltersTooltipShow: t('dataGrid.toolbarFiltersTooltipShow'),
      toolbarFiltersTooltipActive: (count: number) =>
        count !== 1 ? `${count} ${t('dataGrid.toolbarFiltersTooltipActive')}` : `${count} ${t('dataGrid.toolbarFiltersTooltipActive')}`,

      // Export selector toolbar button text
      toolbarExport: t('dataGrid.toolbarExport'),
      toolbarExportLabel: t('dataGrid.toolbarExportLabel'),
      toolbarExportCSV: t('dataGrid.toolbarExportCSV'),
      toolbarExportPrint: t('dataGrid.toolbarExportPrint'),

      // Columns panel text
      columnsPanelTextFieldLabel: t('dataGrid.toolbarColumnsLabel'),
      columnsPanelTextFieldPlaceholder: t('dataGrid.toolbarColumnsLabel'),
      columnsPanelDragIconLabel: 'Reorder column',
      columnsPanelShowAllButton: 'Show all',
      columnsPanelHideAllButton: 'Hide all',

      // Filter panel text
      filterPanelAddFilter: t('dataGrid.filterPanelAddFilter'),
      filterPanelDeleteIconLabel: t('dataGrid.filterPanelDeleteIconLabel'),
      filterPanelLinkOperator: t('dataGrid.filterPanelLinkOperator'),
      filterPanelOperators: t('dataGrid.filterPanelOperators'),
      filterPanelOperatorAnd: t('dataGrid.filterPanelOperatorAnd'),
      filterPanelOperatorOr: t('dataGrid.filterPanelOperatorOr'),
      filterPanelColumns: t('dataGrid.filterPanelColumns'),
      filterPanelInputLabel: t('dataGrid.filterPanelInputLabel'),
      filterPanelInputPlaceholder: t('dataGrid.filterPanelInputPlaceholder'),

      // Filter operators text
      filterOperatorContains: t('dataGrid.filterOperatorContains'),
      filterOperatorEquals: t('dataGrid.filterOperatorEquals'),
      filterOperatorStartsWith: t('dataGrid.filterOperatorStartsWith'),
      filterOperatorEndsWith: t('dataGrid.filterOperatorEndsWith'),
      filterOperatorIs: t('dataGrid.filterOperatorIs'),
      filterOperatorNot: t('dataGrid.filterOperatorNot'),
      filterOperatorAfter: t('dataGrid.filterOperatorAfter'),
      filterOperatorOnOrAfter: t('dataGrid.filterOperatorOnOrAfter'),
      filterOperatorBefore: t('dataGrid.filterOperatorBefore'),
      filterOperatorOnOrBefore: t('dataGrid.filterOperatorOnOrBefore'),
      filterOperatorIsEmpty: t('dataGrid.filterOperatorIsEmpty'),
      filterOperatorIsNotEmpty: t('dataGrid.filterOperatorIsNotEmpty'),
      filterOperatorIsAnyOf: t('dataGrid.filterOperatorIsAnyOf'),

      // Header filter operators text
      headerFilterOperatorContains: t('dataGrid.filterOperatorContains'),
      headerFilterOperatorEquals: t('dataGrid.filterOperatorEquals'),
      headerFilterOperatorStartsWith: t('dataGrid.filterOperatorStartsWith'),
      headerFilterOperatorEndsWith: t('dataGrid.filterOperatorEndsWith'),
      headerFilterOperatorIs: t('dataGrid.filterOperatorIs'),
      headerFilterOperatorNot: t('dataGrid.filterOperatorNot'),
      headerFilterOperatorAfter: t('dataGrid.filterOperatorAfter'),
      headerFilterOperatorOnOrAfter: t('dataGrid.filterOperatorOnOrAfter'),
      headerFilterOperatorBefore: t('dataGrid.filterOperatorBefore'),
      headerFilterOperatorOnOrBefore: t('dataGrid.filterOperatorOnOrBefore'),
      headerFilterOperatorIsEmpty: t('dataGrid.filterOperatorIsEmpty'),
      headerFilterOperatorIsNotEmpty: t('dataGrid.filterOperatorIsNotEmpty'),
      headerFilterOperatorIsAnyOf: t('dataGrid.filterOperatorIsAnyOf'),

      // Column menu text
      columnMenuLabel: t('dataGrid.columnMenuLabel'),
      columnMenuShowColumns: t('dataGrid.columnMenuShowColumns'),
      columnMenuFilter: t('dataGrid.columnMenuFilter'),
      columnMenuHideColumn: t('dataGrid.columnMenuHideColumn'),
      columnMenuUnsort: t('dataGrid.columnMenuUnsort'),
      columnMenuSortAsc: t('dataGrid.columnMenuSortAsc'),
      columnMenuSortDesc: t('dataGrid.columnMenuSortDesc'),

      // Column header text
      columnHeaderFiltersTooltipActive: (count: number) =>
        count !== 1 ? `${count} ${t('dataGrid.columnHeaderFiltersTooltipActive')}` : `${count} ${t('dataGrid.columnHeaderFiltersTooltipActive')}`,
      columnHeaderFiltersLabel: t('dataGrid.columnHeaderFiltersLabel'),
      columnHeaderSortIconLabel: t('dataGrid.columnHeaderSortIconLabel'),

      // Rows selected footer text
      footerRowSelected: (count: number) =>
        count !== 1
          ? `${count.toLocaleString()} ${t('dataGrid.footerRowSelected')}`
          : `${count.toLocaleString()} ${t('dataGrid.footerRowSelected')}`,

      // Total row amount footer text
      footerTotalRows: t('dataGrid.footerTotalRows'),

      // Total visible row amount footer text
      footerTotalVisibleRows: (visibleCount: number, totalCount: number) =>
        `${visibleCount.toLocaleString()} ${t('dataGrid.footerTotalVisibleRows')} ${totalCount.toLocaleString()}`,

      // Checkbox selection text
      checkboxSelectionHeaderName: t('dataGrid.checkboxSelectionHeaderName'),
      checkboxSelectionSelectAllRows: 'Select all rows',
      checkboxSelectionUnselectAllRows: 'Unselect all rows',
      checkboxSelectionSelectRow: 'Select row',
      checkboxSelectionUnselectRow: 'Unselect row',

      // Boolean cell text
      booleanCellTrueLabel: t('dataGrid.booleanCellTrueLabel'),
      booleanCellFalseLabel: t('dataGrid.booleanCellFalseLabel'),

      // Actions cell more text
      actionsCellMore: t('dataGrid.actionsCellMore'),

      // Pagination
      MuiTablePagination: {
        labelRowsPerPage: t('common.rowsPerPage'),
      },
    };
  };

  const value: LanguageContextType = {
    language,
    setLanguage: handleSetLanguage,
    t,
    getDataGridLocaleText,
  };

  return (
    <LanguageContext.Provider value={value}>
      <Helmet>
        <html lang={language} />
        <title>{APP_CONFIG.NAME}</title>
        <meta name="description" content={t('app.description')} />
      </Helmet>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
